import { z } from 'zod';
import type { SessionState } from '../types/types';
import { initialCacheState } from './cacheActions';

// Define schemas for nested objects first.

// Schema for Attack objects used in monsters, NPCs, and party members.
// Represents a single attack with type (e.g., "Claw") and damage (e.g., "1d6+2").
const attackSchema = z.object({
  type: z.string(),
  damage: z.string(),
});

// Schema for geometry objects used in map drawings.
// The geometry is polymorphic - it can represent lines (points array), shapes (rect/circle), or positioned elements.
const geometrySchema = z.object({
  points: z.array(z.object({ x: z.number(), y: z.number() })).optional(),
  posX: z.union([z.number(), z.string()]).transform(val => val !== null ? Number(val) : null).nullable().optional(),
  posY: z.union([z.number(), z.string()]).transform(val => val !== null ? Number(val) : null).nullable().optional(),
  width: z.union([z.number(), z.string()]).transform(val => val !== null ? Number(val) : null).nullable().optional(),
  height: z.union([z.number(), z.string()]).transform(val => val !== null ? Number(val) : null).nullable().optional(),
  cx: z.union([z.number(), z.string()]).transform(val => val !== null ? Number(val) : null).nullable().optional(),
  cy: z.union([z.number(), z.string()]).transform(val => val !== null ? Number(val) : null).nullable().optional(),
  radius: z.union([z.number(), z.string()]).transform(val => val !== null ? Number(val) : null).nullable().optional(),
}).nullable();

// This is the canonical "empty" session state. It's crucial that this object
// has the same shape as the real session state to prevent "cannot read property of undefined" errors.
export const initialSessionState: SessionState = {
  general: {
    activeCampaignId: null, activeSessionId: null, campWatchOrder: [], worldDynamics: '', campaignLog: [],
    gameTime: new Date(), sessionNotes: '', scratchpadNotes: '', sessions: [],
  },
  roster: {
    partyRoster: [], npcs: [], pois: [], relationships: [], encounters: [], currentEvents: [],
    weather: { sky: 'clear', precipitation: 'none', temperature: 25, wind: 'light' },
  },
  combat: {
    isActive: false, setupMode: false, combatants: [], turn: 0, round: 1,
  },
  plots: {
    plots: [],
  },
  map: {
    activeMapId: 1, highlightedMarkerId: null, selectedMarkerId: null,
    drawingMode: null, mapDrawings: [], mapRegions: [],
  },
  lore: { // Renamed from lore.lore to lore.entries for clarity
    entries: [],
  },
  world: {
    worlds: [],
    campaigns: [],
    worldCalendars: {},
    gameSessions: {},
    almanac: [],
    partyTreasures: {},
    moonPhases: {},
    weatherForecasts: {},
    moons: [],
    weatherEvents: [],
  },
  cache: initialCacheState,
};

const dateSchema = z.preprocess(val => (val ? new Date(val as string) : null), z.date().nullable());
const requiredDateSchema = z.preprocess(val => new Date(val as string), z.date());

const generalStateSchema = z.object({
  activeCampaignId: z.number().nullable().default(null),
  activeSessionId: z.number().nullable().default(null),
  campWatchOrder: z.array(z.number()).default([]),
  worldDynamics: z.string().default(""),
  worldDynamicsLastSaved: dateSchema.optional(),  
  campaignLog: z.array(z.object({
    id: z.number(),
    timestamp: z.string(),
    text: z.string(),
    userId: z.number().nullable(),
    user: z.object({ username: z.string() }).nullable(),
  })).default([]),
  gameTime: z.preprocess(val => val ?? new Date().toISOString(), z.string().datetime()).transform(val => new Date(val)),
  sessionNotes: z.string().nullable().default(null),
  scratchpadNotes: z.string().nullable().default(null),
  sessions: z.array(z.object({
    id: z.number(),
    campaignId: z.number(),
    sessionDate: requiredDateSchema,
    gameTimeStart: dateSchema.optional(),
    gameTimeEnd: dateSchema.optional(),
    notes: z.string().nullable().optional(),
    createdAt: requiredDateSchema,
  })).default([]),
});

const itemSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  cost: z.number().nullable(),
  weight: z.number().nullable(),
});

const inventoryItemSchema = z.object({
  id: z.number(),
  quantity: z.number(),
  partyMemberId: z.number(),
  updatedAt: requiredDateSchema,
  itemId: z.number(),
  item: itemSchema,
});

const featureSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  partyMemberId: z.number(),
  updatedAt: requiredDateSchema,
});

const fullSpellSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  level: z.number(),
  school: z.string().nullable(),
  castingTime: z.string().nullable(),
  range: z.string().nullable(),
  components: z.string().nullable(),
  duration: z.string().nullable(),
});

const partySpellSchema = z.object({
  id: z.number(),
  prepared: z.boolean(),
  partyMemberId: z.number(),
  updatedAt: requiredDateSchema,
  spellId: z.number(),
  spell: fullSpellSchema,
});

const partyMemberSchema = z.object({
  id: z.number(),
  name: z.string(),
  color: z.string().nullable(),
  hp: z.number().nullable(),
  maxHp: z.number().nullable(),
  ac: z.number().nullable(),
  dexMod: z.number().nullable(),
  className: z.string().nullable(),
  level: z.number().nullable(),
  race: z.string().nullable(),
  gender: z.string().nullable(),
  notes: z.string().nullable(),
  imageId: z.number().nullable().default(null),
  mapId: z.number().nullable().default(null),
  // posX/posY can be Prisma Decimal (string) or number from client, convert to number
  posX: z.union([z.number(), z.string()]).transform(val => val !== null && val !== undefined ? Number(val) : null).nullable(),
  posY: z.union([z.number(), z.string()]).transform(val => val !== null && val !== undefined ? Number(val) : null).nullable(),
  statStr: z.number().nullable(),
  statDex: z.number().nullable(),
  statCon: z.number().nullable(),
  statInt: z.number().nullable(),
  statWis: z.number().nullable(),
  statCha: z.number().nullable(),
  thac0: z.number().nullable(),
  numberOfAttacks: z.number().nullable(),
  // attacks is a JSON array of Attack objects from the database
  attacks: z.array(attackSchema).nullable(),
  userId: z.number().nullable().default(null),
  inventory: z.array(inventoryItemSchema).default([]),
  features: z.array(featureSchema).default([]),
  spells: z.array(partySpellSchema).default([]),
});

const npcSchema = z.object({
  id: z.number(),
  name: z.string(),
  notes: z.string().nullable().default(null),
  showOnMap: z.boolean(),
  // posX/posY can be Prisma Decimal (string) or number from client, convert to number
  posX: z.union([z.number(), z.string()]).transform(val => val !== null && val !== undefined ? Number(val) : null).nullable(),
  posY: z.union([z.number(), z.string()]).transform(val => val !== null && val !== undefined ? Number(val) : null).nullable(),
  mapId: z.number().nullable().default(null),
  poiId: z.number().nullable().default(null),
  status: z.string().nullable().default(null),
  hp: z.number().nullable().default(null),
  maxHp: z.number().nullable().default(null),
  ac: z.number().nullable().default(null),
  level: z.number().nullable().default(null),
  thac0: z.number().nullable().default(null),
  numberOfAttacks: z.number().nullable().default(null),
  // attacks is a JSON array of Attack objects from the database
  attacks: z.array(attackSchema).nullable().default(null),
  dexMod: z.number().nullable().default(null),
  imageId: z.number().nullable().default(null),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

const poiSchema = z.object({
  id: z.number(),
  name: z.string(),
  notes: z.string().nullable().default(null),
  showOnMap: z.boolean(),
  // posX/posY can be Prisma Decimal (string) or number from client, convert to number
  posX: z.union([z.number(), z.string()]).transform(val => val !== null && val !== undefined ? Number(val) : null).nullable(),
  posY: z.union([z.number(), z.string()]).transform(val => val !== null && val !== undefined ? Number(val) : null).nullable(),
  mapId: z.number().nullable().default(null),
  status: z.string().nullable().default(null),
  deityId: z.number().nullable().default(null),
  imageId: z.number().nullable().default(null),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

const moonSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  cycleDays: z.number(),
  color: z.string().nullable(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

const relationshipSchema = z.object({
  id: z.number(),
  entity1Id: z.number(),
  entity1Type: z.enum(['npc', 'player', 'monster', 'deity']),
  entity2Id: z.number(),
  entity2Type: z.enum(['npc', 'player', 'monster', 'deity']),
  type: z.string(),
  notes: z.string().nullable().optional(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

const encounterCreatureSchema = z.object({
  id: z.number(),
  encounterId: z.number(),
  creatureId: z.number(),
  creatureType: z.string(),
  quantity: z.string(),
  chance: z.number(),
});

const encounterSchema = z.object({
  id: z.number(),
  name: z.string(),
  mapId: z.number(),
  regionId: z.number().nullable().default(null),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
  creatures: z.array(encounterCreatureSchema).default([]),
});

const rosterStateSchema = z.object({
  partyRoster: z.array(partyMemberSchema).default([]),
  npcs: z.array(npcSchema).default([]),
  pois: z.array(poiSchema).default([]),
  relationships: z.array(relationshipSchema).default([]),
  encounters: z.array(encounterSchema).default([]),
  currentEvents: z.array(z.object({ id: z.number(), text: z.string(), sortOrder: z.number() })).default([]),
  weather: z.object({ // This should not have a default, it should be required from the server
    sky: z.string().default("clear"),
    precipitation: z.string().default("none"),
    temperature: z.number().default(25),
    wind: z.string().default("light"),
  }),
});

// =============================================================================
// COMBATANT SCHEMAS - Discriminated Union for Type Safety (T6.2.2)
// =============================================================================
// Combatants represent entities in combat and can be one of:
// - PlayerCombatant: A party member (player character) with full PartyMember properties
// - NonPlayerCombatant: A monster, NPC, or manual entry with flexible properties
//
// We use a discriminated union on 'creatureType' to provide type safety while
// maintaining backward compatibility with existing combat functionality.
// =============================================================================

// Base combatant fields shared by all combatant types.
// These are the core properties required for combat mechanics.
const baseCombatantSchema = z.object({
  id: z.number(),
  creatureId: z.number(),
  name: z.string(),
  hp: z.number().nullable(),
  maxHp: z.number().nullable(),
  ac: z.number().nullable(),
  dexMod: z.number().nullable(),
  initiative: z.number(),
  statusEffects: z.array(z.string()),
  color: z.string().nullable(),
});

// Player combatant schema - represents a party member in combat.
// Extends base combatant with relevant PartyMember fields for player characters.
// Uses .pick() to include only the fields that are commonly used in combat context.
const playerCombatantSchema = baseCombatantSchema.extend({
  creatureType: z.literal('player'),
  // PartyMember fields relevant to combat and character display
  className: z.string().nullable().optional(),
  level: z.number().nullable().optional(),
  race: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  imageId: z.number().nullable().optional(),
  mapId: z.number().nullable().optional(),
  posX: z.union([z.number(), z.string()]).transform(val => val !== null && val !== undefined ? Number(val) : null).nullable().optional(),
  posY: z.union([z.number(), z.string()]).transform(val => val !== null && val !== undefined ? Number(val) : null).nullable().optional(),
  // Ability scores
  statStr: z.number().nullable().optional(),
  statDex: z.number().nullable().optional(),
  statCon: z.number().nullable().optional(),
  statInt: z.number().nullable().optional(),
  statWis: z.number().nullable().optional(),
  statCha: z.number().nullable().optional(),
  // Combat stats
  thac0: z.number().nullable().optional(),
  numberOfAttacks: z.number().nullable().optional(),
  attacks: z.array(attackSchema).nullable().optional(),
  userId: z.number().nullable().optional(),
  // Related collections (may be empty in combat context)
  inventory: z.array(inventoryItemSchema).optional(),
  features: z.array(featureSchema).optional(),
  spells: z.array(partySpellSchema).optional(),
});

// Non-player combatant schema - represents monsters, NPCs, or manual entries in combat.
// Uses passthrough to allow additional properties that may come from Monster or NPC types.
// This maintains backward compatibility with existing combat data structures.
const nonPlayerCombatantSchema = baseCombatantSchema.extend({
  creatureType: z.enum(['monster', 'npc', 'manual']),
  // Common NPC/Monster fields that may be present
  notes: z.string().nullable().optional(),
  imageId: z.number().nullable().optional(),
  mapId: z.number().nullable().optional(),
  posX: z.union([z.number(), z.string()]).transform(val => val !== null && val !== undefined ? Number(val) : null).nullable().optional(),
  posY: z.union([z.number(), z.string()]).transform(val => val !== null && val !== undefined ? Number(val) : null).nullable().optional(),
  hitDice: z.string().nullable().optional(),
  thac0: z.number().nullable().optional(),
  numberOfAttacks: z.number().nullable().optional(),
  attacks: z.array(attackSchema).nullable().optional(),
  status: z.string().nullable().optional(),
  level: z.number().nullable().optional(),
}).passthrough(); // Allow additional properties for monster/NPC-specific fields

// Discriminated union for combatants - provides type safety based on creatureType.
// This allows TypeScript to narrow the type based on the discriminator field.
const combatantSchema = z.discriminatedUnion('creatureType', [
  playerCombatantSchema,
  nonPlayerCombatantSchema,
]);

const combatStateSchema = z.object({
  isActive: z.boolean().default(false),
  setupMode: z.boolean().default(false),
  combatants: z.array(combatantSchema).default([]),
  turn: z.number().default(0),
  round: z.number().default(1),
});

const mapDrawingSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.string(),
  color: z.string(),
  visible: z.boolean(),
  mapId: z.number(),
  // geometry is polymorphic - can contain points for lines, or position/dimensions for shapes
  geometry: geometrySchema.default(null),
  days: z.number().nullable(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

const mapRegionSchema = z.object({
  id: z.number(),
  name: z.string(),
  mapId: z.number(),
  type: z.string(),
  cells: z.preprocess(
    (val) => (typeof val === 'string' ? JSON.parse(val) : val),
    z.array(z.object({ row: z.number(), col: z.number() }))
  ).default([]),
  visible: z.boolean(),
  color: z.string().nullable(),
  // opacity can be Prisma Decimal (string) or number from client, convert to number
  opacity: z.union([z.number(), z.string()]).transform(val => val !== null && val !== undefined ? Number(val) : null).nullable(),
  terrainTypeId: z.number().nullable(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

const weatherConditionSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string(),
  season: z.string().nullable().optional(),
  minTemp: z.number().nullable().optional(),
  maxTemp: z.number().nullable().optional(),
});

const weatherForecastSchema = z.object({
  id: z.number(),
  mapId: z.number(),
  date: requiredDateSchema,
  timePeriod: z.enum(['NIGHT', 'MORNING', 'AFTERNOON', 'EVENING']),
  weatherConditionId: z.number(),
  temperature: z.number().nullable(),
  weatherCondition: weatherConditionSchema.optional(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

const moonPhaseSchema = z.object({
  id: z.number(),
  moonId: z.number(),
  phaseName: z.string(),
  illumination: z.number(),
  date: requiredDateSchema,
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

const mapStateSchema = z.object({
  activeMapId: z.number().nullable().default(1),
  highlightedMarkerId: z.number().nullable().default(null),
  selectedMarkerId: z.number().nullable().default(null),
  drawingMode: z.string().nullable().default(null),
  mapDrawings: z.array(mapDrawingSchema).default([]),
  mapRegions: z.array(mapRegionSchema).default([]),
});

export const fogOfWarSchema = z.object({
  enabled: z.boolean().default(false),
  data: z.string().nullable().default(null),
  eraserSize: z.number().default(40),
});

const plotClueSchema = z.object({
  id: z.number(),
  text: z.string(),
  isFound: z.boolean(),
  plotId: z.number(),
});

const plotProgressPointSchema = z.object({
  id: z.number(),
  description: z.string(),
  isAchieved: z.boolean(),
  plotId: z.number(),
});

const plotSchema = z.object({
  id: z.number(),
  plotName: z.string().refine(val => val, { message: "plot_name is required" }).or(z.string().transform(() => '')).optional().catch('').default(''),
  sortOrder: z.number(),
  completed: z.boolean(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
  imageId: z.number().nullable().default(null),
  clues: z.array(plotClueSchema).default([]),
  progressPoints: z.array(plotProgressPointSchema).default([]),
});

const monsterSchema = z.object({
  id: z.number(),
  name: z.string(),
  hp: z.number(),
  ac: z.number(),
  dexMod: z.number(),
  imageId: z.number().nullable(),
  hitDice: z.string().nullable(),
  thac0: z.number().nullable(),
  numberOfAttacks: z.number().nullable(),
  // attacks is a JSON array of Attack objects from the database
  attacks: z.array(attackSchema).nullable(),
  notes: z.string().nullable(),
  maxHp: z.number().nullable(),
  status: z.string().nullable(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

// Grid settings schema for map images - defines overlay grid configuration.
// This is used for images that serve as maps and need a measurement grid.
const gridSettingsForImageSchema = z.object({
  enabled: z.boolean(),
  type: z.enum(['square', 'hex']),
  size: z.number(),
  offsetX: z.number().optional(),
  offsetY: z.number().optional(),
  opacity: z.union([z.number(), z.string()]).transform(val => Number(val)).default(0.5),
  showLabels: z.boolean().default(true),
  clipToImage: z.boolean().default(true),
  distancePerUnit: z.number().optional(),
  distanceUnit: z.string().optional(),
}).nullable().optional();

const imageSchema = z.object({
  id: z.number(),
  name: z.string(),
  imageUrl: z.string(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
  width: z.number().nullable(),
  height: z.number().nullable(),
  categoryId: z.number().nullable(),
  worldId: z.number().nullable(),
  // Grid settings for map images - optional, only present for images used as maps
  gridSettings: gridSettingsForImageSchema,
});

const treasureSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  value: z.number().nullable(),
  imageId: z.number().nullable(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
  image: imageSchema.nullable().optional(),
});

// Schema for Deity entities from the database
const deitySchema = z.object({
  id: z.number(),
  name: z.string(),
  alignment: z.string().nullable(),
  divineRank: z.string().nullable(),
  domains: z.string().nullable(),
  symbol: z.string().nullable(),
  info: z.string().nullable(),
  imageId: z.number().nullable(),
  symbolImageId: z.number().nullable(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

// Schema for ImageCategory entities from the database
const imageCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  isMapCategory: z.boolean().default(false),
  sortOrder: z.number().default(0),
});

// Schema for TerrainType entities from the database
const terrainTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  color: z.string(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

// Schema for SoundscapeCategory entities from the database
const soundscapeCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
});

// Schema for LoreCategory entities from the database
// Extracted from cacheStateSchema for reusability and type derivation (ADR-0038)
const loreCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  sortOrder: z.number(),
});

// Schema for Soundscape entities from the database
// Extracted from cacheStateSchema for reusability and type derivation (ADR-0038)
const soundscapeSchema = z.object({
  id: z.number(),
  name: z.string(),
  url: z.string(),
  createdAt: requiredDateSchema,
  playCount: z.number().default(0),
  categoryId: z.number().nullable().default(null),
  // Optional category relation - present when joined from database
  category: z.object({ id: z.number(), name: z.string() }).nullable().optional(),
});

// Schema for Lore entries from the database
// Extracted from sessionStateSchema for reusability and type derivation (ADR-0038)
const loreSchema = z.object({
  id: z.number(),
  title: z.string(),
  content: z.string().nullable(),
  categoryId: z.number().nullable(),
  userId: z.number().nullable(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
  category: z.object({ id: z.number(), name: z.string(), sortOrder: z.number() }).nullable(),
  user: z.object({ username: z.string() }).nullable(),
});

const cacheStateSchema = z.object({
  monsterRoster: z.array(monsterSchema).default([]),
  deities: z.array(deitySchema).default([]),
  loreCategories: z.array(loreCategorySchema).default([]),
  imageCategories: z.array(imageCategorySchema).default([]),
  terrainTypes: z.array(terrainTypeSchema).default([]),
  soundscapeCategories: z.array(soundscapeCategorySchema).default([]),
  items: z.array(itemSchema).default([]),
  treasures: z.array(treasureSchema).default([]),
  weatherConditions: z.array(weatherConditionSchema).default([]),
  spells: z.array(fullSpellSchema).default([]),
  soundscapes: z.array(soundscapeSchema).default([]),
});

const worldSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

const campaignSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  worldId: z.number().nullable(),
  currentGameDate: dateSchema,
  scratchpadNotes: z.string().nullable(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
  world: z.object({ name: z.string() }).nullable().optional(),
});

const worldCalendarSchema = z.object({
  id: z.number(),
  worldId: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  yearLengthDays: z.number(),
  months: z.array(z.object({ name: z.string(), days: z.number() })),
});

const partyTreasureSchema = z.object({
  id: z.number(),
  campaignId: z.number(),
  itemId: z.number().nullable(),
  treasureId: z.number().nullable(),
  quantity: z.number(),
  isMagic: z.boolean(),
  isIdentified: z.boolean(),
  isAppraised: z.boolean(),
  notes: z.string().nullable(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
  item: itemSchema.nullable().optional(),
  treasure: treasureSchema.nullable().optional(),
});

const dailyAlmanacSchema = z.object({
  id: z.number(),
  date: requiredDateSchema,
  sunriseTime: requiredDateSchema,
  sunsetTime: requiredDateSchema,
  tempHigh: z.number(),
  tempLow: z.number(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
});

const weatherEventSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  effect: z.string(),
  mapId: z.number().nullable(),
  mapRegionId: z.number().nullable(),
  startDate: dateSchema,
  endDate: dateSchema,
  isActive: z.boolean(),
  createdAt: requiredDateSchema,
  updatedAt: requiredDateSchema,
  map: z.object({ name: z.string() }).optional().nullable(),
  mapRegion: z.object({ name: z.string() }).optional().nullable(),
});

// Define the main schema for the hydratable state.
export const sessionStateSchema = z.object({
  general: generalStateSchema,
  roster: rosterStateSchema,
  combat: combatStateSchema,
  plots: z.object({ plots: z.array(plotSchema).default([]) }).default({ plots: [] }),
  map: mapStateSchema,
  lore: z.object({
    entries: z.array(loreSchema).default([]),
  }).default({ entries: [] }),
  // Global treasure definitions are in cache
  world: z.object({
    worlds: z.array(worldSchema).default([]),
    campaigns: z.array(campaignSchema).default([]),
    worldCalendars: z.record(z.string(), worldCalendarSchema.nullable()).default({}),
    partyTreasures: z.record(z.string(), z.array(partyTreasureSchema)).default({}),
    gameSessions: z.record(z.string(), z.array(generalStateSchema.shape.sessions._def.innerType.element)).default({}),
    almanac: z.array(dailyAlmanacSchema).default([]),
    moons: z.array(moonSchema).default([]),
    moonPhases: z.record(z.string(), z.array(moonPhaseSchema)).default({}),
    weatherForecasts: z.record(z.number(), z.array(weatherForecastSchema)).default({}),
    weatherEvents: z.array(weatherEventSchema).default([]),
  }).default({ worlds: [], campaigns: [], worldCalendars: {}, partyTreasures: {}, gameSessions: {}, almanac: [], weatherEvents: [], moons: [], moonPhases: {}, weatherForecasts: {} }),
  cache: cacheStateSchema.optional().default(initialCacheState),
});

const gridSettingsSchema = z.object({
  enabled: z.boolean(),
  type: z.enum(['square', 'hex']),
  size: z.number(),
  offsetX: z.number().optional(),
  offsetY: z.number().optional(),
  opacity: z.number().default(0.5),
  showLabels: z.boolean().default(true),
  clipToImage: z.boolean().default(true),
  distancePerUnit: z.number().optional(),
  distanceUnit: z.string().optional(),
});

export const settingsSchema = z.object({
  grids: z.record(z.string(), gridSettingsSchema).default({}),
  fogOfWar: fogOfWarSchema.default({ enabled: false, data: null, eraserSize: 40 }),
});

export const hydratableStateSchema = sessionStateSchema.extend({ settings: settingsSchema.optional() });

// =============================================================================
// CACHE-ONLY HYDRATION SCHEMA (ADR-038 Fix)
// =============================================================================
// Used by Phase 2 bootstrap (useCacheBootstrap) when hydrating only cache data.
// This schema validates cache payloads without requiring session structure.
//
// Problem: When useCacheBootstrap calls hydrateState({ cache: {...} }), the
// hydratableStateSchema fails validation because it requires complete session
// structure (general, roster, combat, etc.). This causes console errors even
// though the cache data successfully hydrates.
//
// Solution: Detect cache-only hydration calls and use this separate schema
// that only validates the cache payload structure.
// =============================================================================

/**
 * Schema for cache-only hydration during Phase 2 bootstrap.
 * Used when only cache data is being updated (items, spells, treasures, etc.)
 * without updating session-critical data (general, roster, combat, map).
 *
 * This prevents Zod validation errors when useCacheBootstrap calls
 * hydrateState({ cache }) without the required session fields.
 *
 * All cache fields are optional because the cache bootstrap endpoint may
 * return partial data depending on what's available.
 */
export const cacheOnlyHydrationSchema = z.object({
  cache: cacheStateSchema.partial(),
});

/** Type for cache-only hydration payloads */
export type CacheOnlyHydrationInput = z.infer<typeof cacheOnlyHydrationSchema>;

// Schema for authenticated user identity (ADR-0038)
export const userSchema = z.object({
  id: z.number(),
  username: z.string(),
  email: z.string(),
  role: z.enum(['USER', 'ADMIN']),
});
export type User = z.infer<typeof userSchema>;

// =============================================================================
// TYPE EXPORTS - Derived from Zod schemas via z.infer (ADR-0038)
// =============================================================================
// These types are the single source of truth for runtime-validated entities.
// They represent the TRANSFORMED data after Zod parsing (e.g., Decimal -> number).
// For raw API input types (before transformation), see the Input types below.
// =============================================================================

// ---- Primitive/Nested Entity Types ----

/** Attack object used in monsters, NPCs, and party members */
export type Attack = z.infer<typeof attackSchema>;

/** Inventory item with nested Item reference */
export type InventoryItem = z.infer<typeof inventoryItemSchema>;

/** Character feature (ability/trait) */
export type Feature = z.infer<typeof featureSchema>;

/** Party member's prepared/known spell with nested Spell reference */
export type PartySpell = z.infer<typeof partySpellSchema>;

/** Plot clue - a discoverable hint within a plot */
export type PlotClue = z.infer<typeof plotClueSchema>;

/** Plot progress point - a milestone within a plot */
export type PlotProgressPoint = z.infer<typeof plotProgressPointSchema>;

/** Creature within an encounter table */
export type EncounterCreature = z.infer<typeof encounterCreatureSchema>;

// ---- Core Entity Types ----

/** Party member (player character) with inventory, features, and spells */
export type PartyMember = z.infer<typeof partyMemberSchema>;

/** Non-player character */
export type NPC = z.infer<typeof npcSchema>;

/** Point of Interest on a map */
export type POI = z.infer<typeof poiSchema>;

/** Monster from the monster roster */
export type Monster = z.infer<typeof monsterSchema>;

/** Map region (terrain/zone on a map) */
export type MapRegion = z.infer<typeof mapRegionSchema>;

/** Drawing overlay on a map (lines, shapes, etc.) */
export type MapDrawing = z.infer<typeof mapDrawingSchema>;

/** Plot/quest with clues and progress points */
export type Plot = z.infer<typeof plotSchema>;

/** Random encounter table */
export type Encounter = z.infer<typeof encounterSchema>;

// ---- Combatant Types (Discriminated Union) ----
// Note: NonPlayerCombatant and Combatant types are manually defined to avoid
// the index signature that z.infer adds when using .passthrough().
// This ensures type compatibility with the discriminated union pattern.

/** Base combatant fields shared by all combatant types */
export type BaseCombatant = z.infer<typeof baseCombatantSchema>;

/** Player combatant - a party member participating in combat */
export type PlayerCombatant = z.infer<typeof playerCombatantSchema>;

/**
 * Non-player combatant - a monster, NPC, or manual entry in combat.
 * Manually defined to exclude index signature from passthrough schema.
 * Includes base combatant fields plus optional NPC/Monster properties.
 */
export type NonPlayerCombatant = BaseCombatant & {
  creatureType: 'monster' | 'npc' | 'manual';
  // Optional fields that may be present from Monster or NPC types
  notes?: string | null;
  imageId?: number | null;
  mapId?: number | null;
  posX?: number | null;
  posY?: number | null;
  hitDice?: string | null;
  thac0?: number | null;
  numberOfAttacks?: number | null;
  attacks?: Attack[] | null;
  status?: string | null;
  level?: number | null;
};

/**
 * Combat participant - discriminated union of player and non-player combatants.
 * Uses creatureType as the discriminator field.
 */
export type Combatant = PlayerCombatant | NonPlayerCombatant;

/** Relationship between entities (NPC-NPC, player-deity, etc.) */
export type Relationship = z.infer<typeof relationshipSchema>;

// ---- World/Campaign Entity Types ----

/** Weather condition definition */
export type WeatherCondition = z.infer<typeof weatherConditionSchema>;

/** Weather forecast for a specific time period */
export type WeatherForecast = z.infer<typeof weatherForecastSchema>;

/** Moon celestial body */
export type Moon = z.infer<typeof moonSchema>;

/** Moon phase for a specific date */
export type MoonPhase = z.infer<typeof moonPhaseSchema>;

/** Weather event affecting a region */
export type WeatherEvent = z.infer<typeof weatherEventSchema>;

/** Daily almanac with sunrise/sunset and temperatures */
export type DailyAlmanac = z.infer<typeof dailyAlmanacSchema>;

/** World (campaign setting/universe) */
export type World = z.infer<typeof worldSchema>;

/** Campaign within a world */
export type Campaign = z.infer<typeof campaignSchema>;

/** Calendar system for a world */
export type WorldCalendar = z.infer<typeof worldCalendarSchema>;

/** Deity/god entity */
export type Deity = z.infer<typeof deitySchema>;

// ---- Cache/Reference Entity Types ----

/** Image in the image library */
export type Image = z.infer<typeof imageSchema>;

/** Treasure item */
export type Treasure = z.infer<typeof treasureSchema>;

/** Item in the item catalog */
export type Item = z.infer<typeof itemSchema>;

/** Spell in the spell catalog */
export type Spell = z.infer<typeof fullSpellSchema>;

/** Party treasure (loot held by the party) */
export type PartyTreasure = z.infer<typeof partyTreasureSchema>;

/** Lore entry (knowledge article) */
export type Lore = z.infer<typeof loreSchema>;

/** Soundscape audio file */
export type Soundscape = z.infer<typeof soundscapeSchema>;

// ---- Category Entity Types ----

/** Category for lore entries */
export type LoreCategory = z.infer<typeof loreCategorySchema>;

/** Category for images */
export type ImageCategory = z.infer<typeof imageCategorySchema>;

/** Category for soundscapes */
export type SoundscapeCategory = z.infer<typeof soundscapeCategorySchema>;

/** Terrain type for map regions */
export type TerrainType = z.infer<typeof terrainTypeSchema>;

// =============================================================================
// INPUT TYPE EXPORTS - For schemas with transforms (ADR-0038 Pattern 3)
// =============================================================================
// Schemas with .transform() or .preprocess() have different input vs output types.
// Use Input types when handling raw API responses before Zod parsing.
// Use the standard types (above) for validated/transformed data in application code.
// =============================================================================

/**
 * PartyMember input type (before Decimal->number transformation).
 * Use this when handling raw API responses before Zod validation.
 * posX/posY fields accept both number and string (Prisma Decimal).
 */
export type PartyMemberInput = z.input<typeof partyMemberSchema>;

/**
 * NPC input type (before Decimal->number transformation).
 * Use this when handling raw API responses before Zod validation.
 * posX/posY fields accept both number and string (Prisma Decimal).
 */
export type NPCInput = z.input<typeof npcSchema>;

/**
 * POI input type (before Decimal->number transformation).
 * Use this when handling raw API responses before Zod validation.
 * posX/posY fields accept both number and string (Prisma Decimal).
 */
export type POIInput = z.input<typeof poiSchema>;

/**
 * MapRegion input type (before transformations).
 * Use this when handling raw API responses before Zod validation.
 * - cells: accepts JSON string or parsed array
 * - opacity: accepts both number and string (Prisma Decimal)
 */
export type MapRegionInput = z.input<typeof mapRegionSchema>;

/**
 * MapDrawing input type (before geometry transformations).
 * Use this when handling raw API responses before Zod validation.
 * geometry.posX/posY/width/height/cx/cy/radius accept both number and string.
 */
export type MapDrawingInput = z.input<typeof mapDrawingSchema>;